import {
  Autocomplete,
  Button,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Search } from '@mui/icons-material'
import { useState } from 'react'
import { useSearch } from 'api/hooks/use-search'
import { grey } from '@mui/material/colors'
import { useDebounce } from 'lib/hooks/use-debounce'
import { useTranslation } from 'react-i18next'
import { useUrlQuery } from 'lib/hooks/use-url-query'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { pageConfig } from 'app/page-config'
import { ISearch } from 'api/types/search'
import { useBoolean } from 'lib/hooks/use-boolean'

export const SearchAutocomplete = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { subCategory } = useParams()
  const open = useBoolean(false)
  const { setUrlQuery } = useUrlQuery()
  const [value, setValue] = useState<any | null>(null)
  const [inputValue, setInputValue] = useState('')
  const debouncedSearch = useDebounce(inputValue)
  const { data: options, isLoading } = useSearch({
    query: debouncedSearch,
    enabled: !!debouncedSearch,
  })

  const handleClick = ({
    id,
    isCategory,
    label,
    categoryId,
    subCategoryId,
  }: ISearch) => {
    setValue(null)
    setInputValue('')

    if (isCategory) {
      navigate(`${pageConfig.products}/${categoryId}/${subCategoryId}`)
    } else if (
      !location.pathname.startsWith(pageConfig.products) ||
      subCategory !== subCategoryId
    ) {
      navigate({
        pathname: `${pageConfig.products}/${categoryId}/${subCategoryId}`,
        search: createSearchParams({
          query: `${label}-${id}`,
        }).toString(),
      })
    } else {
      setUrlQuery((prev) => {
        prev.delete('filters')
        prev.set('query', `${label}-${id}`)

        return prev
      })
    }
  }

  return (
    <Stack
      onMouseEnter={() => {
        if (inputValue) {
          open.setTrue()
        }
      }}
      onMouseLeave={open.setFalse}
      direction="row"
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: '400px',
        backgroundColor: theme.palette.action.focus,
        padding: '5px',
        borderRadius: 0.5,
      }}
    >
      <Autocomplete
        open={open.isTrue}
        onOpen={open.setTrue}
        onClose={open.setFalse}
        loading={isLoading}
        loadingText={t('search')}
        noOptionsText={t('no_matches')}
        filterOptions={(value: Array<ISearch>) => value}
        value={value}
        disableClearable
        onChange={(_event, newValue) => {
          if (newValue) {
            handleClick(newValue)
          }
        }}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue)
        }}
        options={options}
        renderOption={(props, { label, description }: ISearch) => {
          const { key, ...optionProps } = props

          return (
            <MenuItem
              key={key}
              {...optionProps}
              sx={{ '&.MuiMenuItem-root': { py: '5px' } }}
            >
              <Typography sx={{ textWrap: 'wrap' }}>
                {description ? `${label} - ${description}` : label}
              </Typography>
            </MenuItem>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              '& .MuiInputBase-root.MuiOutlinedInput-root': {
                py: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: theme.palette.common.white,
              },
              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                paddingTop: 0.5,
                paddingBottom: 0.5,
              },
              '&.MuiTextField-root .MuiOutlinedInput-root > fieldset': {
                borderRight: 'none',
                boxShadow: 'none',
              },
              '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
                width: 22,
                height: 22,
              },
              '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                width: 16,
                height: 16,
              },
              '& .MuiAutocomplete-endAdornment button:last-of-type svg': {
                width: 22,
                height: 22,
              },
            }}
            placeholder={t('search')}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment
                    position="start"
                    style={{
                      marginLeft: '4px',
                      marginRight: 0,
                      pointerEvents: 'none',
                    }}
                  >
                    <Search sx={{ color: grey[500] }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        sx={{ flexGrow: 1 }}
      />
      <Button
        variant="contained"
        color="success"
        sx={{
          height: '28px',
          background:
            'linear-gradient(0deg, rgba(118,191,41,1) 50%, rgba(150,244,50,1) 100%)',
          border: '1px solid rgba(118, 191, 41, 1)',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }}
      >
        {t('find')}
      </Button>
    </Stack>
  )
}
